import React, { useEffect, useState } from 'react';
import ClassicScreenShell from './ClassicScreenShell';
import { getErrorMessage } from '../utils/errorHandler';

interface SystemCloseProps {
    onMenuSelect: (menuId: string) => void;
    tenantName: string;
    currentUser: any;
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
}

interface LockStatus {
    month: number;
    is_closed: boolean;
    closed_at: string | null;
    closed_by_name: string | null;
}

const SystemClose: React.FC<SystemCloseProps> = ({
    onMenuSelect,
    tenantName,
    currentUser,
    apiFetch,
    currentTenantId,
}) => {
    const [selectedFiscalYear, setSelectedFiscalYear] = useState<number>(new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1);
    const [locks, setLocks] = useState<LockStatus[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchLocks = async () => {
        if (!currentTenantId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await apiFetch(`/api/locks?fiscal_year=${selectedFiscalYear}`);
            if (!res.ok) {
                throw new Error(await getErrorMessage(res, "ロック情報の取得に失敗しました"));
            }
            setLocks(await res.json());
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLocks();
    }, [selectedFiscalYear, currentTenantId]);

    const handleToggleLock = async (month: number, currentStatus: boolean) => {
        if (!window.confirm(`${selectedFiscalYear}年度 ${month === 13 ? '年次決算' : month + '月'}を${currentStatus ? '解除' : '締切'}しますか？`)) {
            return;
        }

        try {
            const res = await apiFetch(`/api/locks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fiscal_year: selectedFiscalYear,
                    month: month,
                    is_closed: !currentStatus
                })
            });

            if (!res.ok) {
                throw new Error(await getErrorMessage(res, "更新に失敗しました"));
            }

            await fetchLocks();
        } catch (err: any) {
            alert(err.message);
        }
    };

    // 4月〜3月、13(年次)の順に並べる
    const sortedLocks = [...locks].sort((a, b) => {
        const orderA = a.month === 13 ? 99 : (a.month >= 4 ? a.month - 3 : a.month + 9);
        const orderB = b.month === 13 ? 99 : (b.month >= 4 ? b.month - 3 : b.month + 9);
        return orderA - orderB;
    });

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', backgroundColor: '#fff', border: '2px solid #ccc' }}>
            <div style={{ marginBottom: '20px', backgroundColor: '#f0f0f0', padding: '10px' }}>
                <strong>会計年度: </strong>
                <select
                    value={selectedFiscalYear}
                    onChange={(e) => setSelectedFiscalYear(Number(e.target.value))}
                    style={{ padding: '4px' }}
                >
                    {[...Array(5)].map((_, i) => {
                        const y = new Date().getFullYear() - 2 + i;
                        return <option key={y} value={y}>{y}年度</option>;
                    })}
                </select>
                <button onClick={fetchLocks} style={{ marginLeft: '10px', padding: '4px 8px' }}>更新</button>
            </div>

            {error && <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                    <tr style={{ backgroundColor: '#0066cc', color: 'white' }}>
                        <th style={{ padding: '8px', border: '1px solid #999' }}>対象月</th>
                        <th style={{ padding: '8px', border: '1px solid #999' }}>状態</th>
                        <th style={{ padding: '8px', border: '1px solid #999' }}>ロック日時</th>
                        <th style={{ padding: '8px', border: '1px solid #999' }}>実行者</th>
                        <th style={{ padding: '8px', border: '1px solid #999' }}>操作</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedLocks.map((lock) => (
                        <tr key={lock.month} style={{ backgroundColor: lock.is_closed ? '#ffeeba' : 'white' }}>
                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                {lock.month === 13 ? '年次決算' : `${lock.month}月`}
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', color: lock.is_closed ? '#d9534f' : '#5cb85c', fontWeight: 'bold' }}>
                                {lock.is_closed ? '締切済' : '入力可'}
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                {lock.closed_at ? new Date(lock.closed_at).toLocaleString() : '-'}
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                {lock.closed_by_name || '-'}
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                <button
                                    onClick={() => handleToggleLock(lock.month, lock.is_closed)}
                                    style={{
                                        backgroundColor: lock.is_closed ? '#d9534f' : '#0275d8',
                                        color: 'white',
                                        border: 'none',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {lock.is_closed ? '解除' : '締切'}
                                </button>
                            </td>
                        </tr>
                    ))}
                    {sortedLocks.length === 0 && !loading && (
                        <tr><td colSpan={5} style={{ padding: '20px', textAlign: 'center' }}>データがありません</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default SystemClose;
